import React, { useState, useMemo } from 'react';
import '../estilos/Recepcion.css';

const ProgresoRecepcion = ({ certificados = [], completados = [] }) => {
  const [seccionesExpandidas, setSeccionesExpandidas] = useState({});
  const [partidasExpandidas, setPartidasExpandidas] = useState({});

  // Combinar certificados activos y completados en una sola lista
  // Los completados tienen todos sus documentos marcados como completado=true
  const todasLasPartidas = useMemo(() => {
    const activos = certificados.map(c => ({
      ...c,
      _origen: 'activo'
    }));
    const terminados = completados.map(c => ({
      ...c,
      _origen: 'completado',
      documentos: (c.documentos || []).map(d => ({ ...d, completado: true }))
    }));
    return [...activos, ...terminados];
  }, [certificados, completados]);

  // Agrupar por sección (especialidad) → partidas
  const datosAgrupados = useMemo(() => {
    const ordenSecciones = [];
    const grupos = {};
    todasLasPartidas.forEach((cert) => {
      const seccion = cert.especialidad?.trim() || 'Sin Sección';
      if (!grupos[seccion]) {
        grupos[seccion] = [];
        ordenSecciones.push(seccion);
      }
      grupos[seccion].push(cert);
    });
    return { ordenSecciones, grupos };
  }, [todasLasPartidas]);

  // Calcular progreso de una partida (basado en documentos individuales)
  const getProgreso = (cert) => {
    const docs = cert.documentos || [];
    if (docs.length === 0) return { completados: 0, total: 0, pendientes: 0, porcentaje: 0 };
    const docsCompletados = docs.filter(d => d.completado).length;
    return {
      completados: docsCompletados,
      total: docs.length,
      pendientes: docs.length - docsCompletados,
      porcentaje: Math.round((docsCompletados / docs.length) * 100)
    };
  };

  // Calcular progreso de una sección (suma de documentos de todas las partidas)
  const getProgresoSeccion = (partidas) => {
    let totalDocs = 0;
    let docsCompletados = 0;
    partidas.forEach(cert => {
      const docs = cert.documentos || [];
      totalDocs += docs.length;
      docsCompletados += docs.filter(d => d.completado).length;
    });
    const pendientes = totalDocs - docsCompletados;
    return {
      completados: docsCompletados,
      total: totalDocs,
      pendientes,
      porcentaje: totalDocs > 0 ? Math.round((docsCompletados / totalDocs) * 100) : 0
    };
  };

  // Color de la barra según porcentaje: semáforo verde/amarillo/rojo
  const getBarColor = (porcentaje) => {
    if (porcentaje === 100) return '#22c55e';
    if (porcentaje > 0) return '#eab308';
    return '#ef4444';
  };

  // Progreso global del proyecto
  const progresoGlobal = useMemo(() => getProgresoSeccion(todasLasPartidas), [todasLasPartidas]);

  const mesesCortos = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  const formatearFecha = (fechaStr) => {
    if (!fechaStr) return '—';
    const str = typeof fechaStr === 'string' ? fechaStr : new Date(fechaStr).toISOString().split('T')[0];
    const partes = str.split('-');
    if (partes.length !== 3) return str;
    return `${partes[2]}/${mesesCortos[parseInt(partes[1], 10) - 1] || partes[1]}/${partes[0]}`;
  };

  const toggleSeccion = (seccion) => {
    setSeccionesExpandidas(prev => ({ ...prev, [seccion]: !prev[seccion] }));
  };

  const togglePartida = (certId) => {
    setPartidasExpandidas(prev => ({ ...prev, [certId]: !prev[certId] }));
  };

  const { ordenSecciones, grupos } = datosAgrupados;

  return (
    <div className="tab-content">
      <section className="recepcion-placeholder-section">
        <div className="recepcion-header-bar">
          <span className="recepcion-badge">RECEPCION</span>
          <span className="recepcion-header-title">Progreso de Recepciones Finales</span>
          <span style={{ marginLeft: 'auto', fontSize: '13px', color: '#6b7280' }}>
            {progresoGlobal.completados}/{progresoGlobal.total} documentos · {todasLasPartidas.length} partida{todasLasPartidas.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Progreso global del proyecto */}
        <div style={{ padding: '0 0 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
            <span className="recepcion-progreso-texto" style={{ fontWeight: 700 }}>Progreso General del Proyecto</span>
            <span className="recepcion-progreso-texto" style={{ marginLeft: 'auto', fontWeight: 700, color: getBarColor(progresoGlobal.porcentaje) }}>
              {progresoGlobal.porcentaje}%
            </span>
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {progresoGlobal.completados} completado{progresoGlobal.completados !== 1 ? 's' : ''} · {progresoGlobal.pendientes} pendiente{progresoGlobal.pendientes !== 1 ? 's' : ''} · {progresoGlobal.total} total
          </div>
        </div>

        {todasLasPartidas.length === 0 ? (
          <div className="recepcion-empty-state">
            <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: '14px', padding: '40px 0' }}>
              No hay documentos de recepción final registrados. Agrega partidas y documentos en la Matriz de Recepcion.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {ordenSecciones.map((seccion) => {
              const partidas = grupos[seccion];
              const progresoSec = getProgresoSeccion(partidas);
              const expandida = seccionesExpandidas[seccion] !== false; // Expandida por defecto

              return (
                <div key={seccion} style={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
                  {/* Header de sección */}
                  <div
                    className="recepcion-progreso-seccion-header"
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => toggleSeccion(seccion)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '12px', transition: 'transform 0.2s', transform: expandida ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                      <span style={{ fontWeight: 700 }}>{seccion}</span>
                    </div>
                    <div className="recepcion-progreso-seccion-stats">
                      <span>{partidas.length} partida{partidas.length !== 1 ? 's' : ''}</span>
                      <span style={{ fontWeight: 600 }}>{progresoSec.completados}/{progresoSec.total} docs</span>
                      <span>({progresoSec.pendientes} pendiente{progresoSec.pendientes !== 1 ? 's' : ''})</span>
                      <span style={{ fontWeight: 700, color: getBarColor(progresoSec.porcentaje) }}>
                        {progresoSec.porcentaje}%
                      </span>
                    </div>
                  </div>

                  {/* Partidas de la sección */}
                  {expandida && (
                    <div style={{ padding: '0 12px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {partidas.map((cert) => {
                        const progreso = getProgreso(cert);
                        const certId = cert._id || cert.id;
                        const esPartidaCompleta = progreso.porcentaje === 100;
                        const partidaExpandida = partidasExpandidas[certId] !== undefined ? partidasExpandidas[certId] : false;

                        return (
                          <div key={certId} className={`recepcion-progreso-card${esPartidaCompleta ? ' recepcion-progreso-card-completada' : ''}`}>
                            {/* Header de partida */}
                            <div
                              style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', cursor: 'pointer' }}
                              onClick={() => togglePartida(certId)}
                            >
                              {/* Info de la partida */}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 700, fontSize: '14px', color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ fontSize: '10px', transition: 'transform 0.2s', transform: partidaExpandida ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                                  <span>{cert.nombrePartida || 'Sin nombre'}</span>
                                  <span className="recepcion-progreso-barra-container" style={{ width: '56px', height: '5px', flexShrink: 0 }}>
                                    <div className="recepcion-progreso-barra-fill" style={{ width: `${progreso.porcentaje}%`, background: getBarColor(progreso.porcentaje) }} />
                                  </span>
                                </div>
                                {cert.terminoPartida && (
                                  <div style={{ fontSize: '12px', color: '#6b7280', marginLeft: '18px' }}>
                                    Término: {formatearFecha(cert.terminoPartida)}
                                  </div>
                                )}
                              </div>

                              {/* Stats de la partida */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                                <div style={{ textAlign: 'right', fontSize: '12px', lineHeight: '1.4' }}>
                                  <div style={{ fontWeight: 700, color: getBarColor(progreso.porcentaje) }}>
                                    {progreso.completados} / {progreso.total}
                                  </div>
                                  <div style={{ color: '#9ca3af', fontSize: '10px' }}>
                                    {progreso.pendientes > 0 ? `${progreso.pendientes} pendiente${progreso.pendientes !== 1 ? 's' : ''}` : 'Completa'}
                                  </div>
                                </div>

                                {/* Badge porcentaje / check de completado */}
                                {esPartidaCompleta ? (
                                  <span title="Completado" style={{
                                    width: '22px', height: '22px', borderRadius: '50%',
                                    background: '#22c55e', color: '#fff',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '13px', fontWeight: 700, flexShrink: 0
                                  }}>
                                    ✓
                                  </span>
                                ) : (
                                  <span style={{
                                    padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
                                    fontWeight: 700,
                                    background: progreso.porcentaje > 0 ? '#fef9c3' : '#fef2f2',
                                    color: progreso.porcentaje > 0 ? '#a16207' : '#dc2626'
                                  }}>
                                    {progreso.porcentaje}%
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Lista de documentos (expandible) */}
                            {partidaExpandida && progreso.total > 0 && (
                              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                {(cert.documentos || []).map((doc, docIdx) => (
                                  <div key={doc._id || doc.id || docIdx} style={{
                                    display: 'flex', alignItems: 'center', gap: '8px',
                                    padding: '5px 10px', borderRadius: '6px', fontSize: '12px',
                                    background: doc.completado ? '#f9fafb' : '#fff',
                                    border: '1px solid #e5e7eb'
                                  }}>
                                    <span style={{ color: doc.completado ? '#111827' : '#d1d5db', fontWeight: 700, fontSize: '14px' }}>
                                      {doc.completado ? '✓' : '○'}
                                    </span>
                                    <span style={{
                                      flex: 1, color: doc.completado ? '#6b7280' : '#374151',
                                      textDecoration: doc.completado ? 'line-through' : 'none'
                                    }}>
                                      {doc.nombre || `Documento ${docIdx + 1}`}
                                    </span>
                                    <span style={{
                                      fontSize: '10px', fontWeight: 600,
                                      color: doc.completado ? '#374151' : '#6b7280',
                                      padding: '1px 6px', borderRadius: '8px',
                                      background: '#f3f4f6'
                                    }}>
                                      {doc.completado ? 'Completado' : 'Pendiente'}
                                    </span>
                                    {doc.completado && doc.fechaCompletado && (
                                      <span style={{ fontSize: '10px', color: '#9ca3af' }}>
                                        {formatearFecha(doc.fechaCompletado)}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default ProgresoRecepcion;
